import { useCallback, useEffect, useLayoutEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Download } from 'lucide-react'
import { Alert, Button, SegmentedControl, SkeletonList } from '@/components/ui'
import { PageHeader, QueryBoundary } from '@/components/domain'
import { useT } from '@/internationalization/i18n'
import { PatronFilter } from '@/features/patrons'
import { ActionItemList } from './components/ActionItemList'
import { DueFilter } from './components/DueFilter'
import { MeetingFilter } from './components/MeetingFilter'
import { useActionItems } from './hooks'

const STATUS_OPTIONS = [
  { value: 'all', key: 'actions.status.all' },
  { value: 'open', key: 'actions.status.open' },
  { value: 'in_progress', key: 'actions.status.inProgress' },
  { value: 'done', key: 'actions.status.done' },
]

const OWNER_OPTIONS = [
  { value: 'all', key: 'actions.owner.all' },
  { value: 'me', key: 'actions.owner.me' },
  { value: 'unassigned', key: 'actions.owner.unassigned' },
]

/**
 * The viewer's calendar day as `YYYY-MM-DD`, built from LOCAL parts.
 *
 * `toISOString().slice(0, 10)` is the obvious one-liner and is wrong: it
 * converts to UTC first, so from 05:30 IST onwards it is already correct and
 * before then it names yesterday — and in the Americas it names tomorrow for
 * most of the working day. Since the whole reason this value is sent is that
 * the server's UTC day is the wrong day, computing it via UTC would leave the
 * bug exactly where it was.
 */
function localToday(): string {
  const now = new Date()
  const month = `${now.getMonth() + 1}`.padStart(2, '0')
  const day = `${now.getDate()}`.padStart(2, '0')
  return `${now.getFullYear()}-${month}-${day}`
}

/**
 * Every commitment across every meeting.
 *
 * "Unassigned" is a first-class filter, not a footnote: those are the items the
 * summariser could not match to a participant, and they are the ones most
 * likely to be dropped entirely. So is "No due date", for the same reason one
 * step along — an item nobody has dated sorts last everywhere else and is
 * therefore invisible.
 *
 * Every filter is applied SERVER-SIDE, so the total belongs to the filtered
 * set. Filtering a page of 50 in the browser would silently mean "the overdue
 * items among the 50 most recent", which reads identically to "the overdue
 * items" and is a different, smaller answer.
 */
export function ActionsPage() {
  const t = useT()
  const [searchParams, setSearchParams] = useSearchParams()
  // A notification's deep-link -- one specific item. `status` defaults to
  // 'all' rather than 'open' when it is set, so a link to a `done` or
  // `dismissed` item does not land on an empty list.
  const actionItemId = searchParams.get('actionItemId') ?? undefined
  // The dashboard's link -- one item to FIND in the full list, not to narrow
  // it to. Unlike `actionItemId` it filters nothing: the list stays whole and
  // `ActionItemList` scrolls to and highlights the item.
  const highlightId = searchParams.get('highlight') ?? undefined
  // Nothing in the router resets the window's scroll on navigation, so arriving
  // from a scrolled page lands part-way down. Reset on arrival -- but not for a
  // highlight, where `ActionItemList` scrolls to the item once the list loads.
  useLayoutEffect(() => {
    if (!highlightId) window.scrollTo(0, 0)
    // Arrival only: a later `?highlight=` change must not yank the page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const [status, setStatus] = useState(actionItemId ? 'all' : 'open')
  const [owner, setOwner] = useState('all')
  const [patron, setPatron] = useState('all')
  const [meetingId, setMeetingId] = useState('all')
  const [due, setDue] = useState('all')

  // The page stays mounted across notifications (same route, different
  // `?actionItemId=`), so a `useState` initializer alone only seeds these on
  // the very first mount. Without this, clicking a second notification while
  // the page is already open keeps whatever filters were left over from
  // before -- narrowing the new deep-link against unrelated owner/patron/
  // meeting/due filters and showing an empty list instead of the item that
  // was clicked.
  useEffect(() => {
    if (actionItemId) {
      setStatus('all')
      setOwner('all')
      setPatron('all')
      setMeetingId('all')
      setDue('all')
    }
  }, [actionItemId])

  // `actionItemId` narrows the server-side query to one item, so as long as
  // it is in the URL every other control here is applied on top of that one
  // item and can look broken -- changing Status or Owner never changes what
  // is on screen. Touching any filter is a signal the viewer wants to browse
  // again, so it drops the deep-link from the URL rather than leaving it to
  // silently keep overriding everything. `replace: true` so the back button
  // returns to wherever the notification came from, not to this filter step.
  const clearDeepLink = useCallback(() => {
    if (!actionItemId) return
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.delete('actionItemId')
        return next
      },
      { replace: true },
    )
  }, [actionItemId, setSearchParams])

  // The deep link is what forced Status to 'all'. Touching any OTHER filter
  // clears the link but must also let Status fall back to its normal 'open'
  // default -- otherwise it stays stuck on 'all' with nothing on screen
  // explaining why. The Status control's own handler does not use this: the
  // user there is explicitly choosing a value, and this would overwrite it.
  const clearDeepLinkAndStatus = useCallback(() => {
    if (actionItemId) setStatus('open')
    clearDeepLink()
  }, [actionItemId, clearDeepLink])

  // `today` is in the params, and therefore in the query key, whether or not a
  // due filter is active: a tab left open past midnight otherwise keeps
  // serving yesterday's buckets from cache.
  const params = { status, owner, patron, meetingId, due, today: localToday(), actionItemId }
  const { data, isPending, error, refetch } = useActionItems(params)

  return (
    <div>
      <PageHeader
        title={t('actions.title')}
        description={t('actions.description')}
        actions={
          <Button leftIcon={<Download className="h-4 w-4" />}>{t('common.exportCsv')}</Button>
        }
        below={
          <div className="flex flex-wrap items-center gap-2">
            <SegmentedControl
              label={t('actions.status')}
              options={STATUS_OPTIONS.map((o) => ({ value: o.value, label: t(o.key) }))}
              value={status}
              onChange={(v) => {
                setStatus(v)
                clearDeepLink()
              }}
            />
            <SegmentedControl
              label={t('actions.owner')}
              options={OWNER_OPTIONS.map((o) => ({ value: o.value, label: t(o.key) }))}
              value={owner}
              onChange={(v) => {
                setOwner(v)
                clearDeepLinkAndStatus()
              }}
            />

            <DueFilter
              value={due}
              onChange={(v) => {
                setDue(v)
                clearDeepLinkAndStatus()
              }}
            />
            <MeetingFilter
              value={meetingId}
              onChange={(v) => {
                setMeetingId(v)
                clearDeepLinkAndStatus()
              }}
            />
            <PatronFilter
              value={patron}
              onChange={(v) => {
                setPatron(v)
                clearDeepLinkAndStatus()
              }}
            />
          </div>
        }
      />
      {actionItemId && (
        <Alert
          tone="info"
          title={t('actions.deepLinkTitle')}
          action={
            <Button size="sm" variant="secondary" onClick={clearDeepLinkAndStatus}>
              {t('actions.deepLinkClear')}
            </Button>
          }
          className="mt-4"
        />
      )}
      <QueryBoundary
        isLoading={isPending}
        error={error}
        onRetry={refetch}
        skeleton={<SkeletonList rows={6} className="sb-panel p-4" />}
      >
        <ActionItemList
          items={data?.items ?? []}
          context="workspace"
          filterParams={params}
          highlightId={highlightId}
        />
      </QueryBoundary>
    </div>
  )
}
